import { User } from 'oidc-client-ts';

import { signTestJwt } from './signTestJwt';

const NAME_IDENTIFIER_CLAIM =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

const TOKEN_LIFETIME_SECONDS = 12 * 60 * 60;

// Default tenant TLP base for test-login. The e2e MSW worker registers handlers
// against `http://mockapi/api/v2/*`, so this lets the legacy fetcher's
// `tlpBase()` resolve to MSW without needing `VITE_TLP_URL` set.
const DEFAULT_APP_URL = 'http://mockapi';

export interface TestIdentity {
  tenant: string;
  userId: string | number;
  site: string | number;
  appUrl?: string;
}

export interface TestLoginConfig {
  secret: string;
  issuer: string;
  audience: string;
}

/**
 * Builds an oidc-client-ts `User` for the test-login flow. The access token is
 * an HS256-signed JWT with `nameidentifier = "{tenant}:{userId}:{site}"` —
 * matching the format Phoenix/TLP expect. `app_url` is embedded in both the
 * payload and profile so `gateway.tlpBase()` returns the right legacy backend
 * (mirrors the production id token, which carries the per-tenant TLP host).
 */
export const createTestUser = async (
  identity: TestIdentity,
  config: TestLoginConfig,
): Promise<User> => {
  const sub = `${identity.tenant}:${identity.userId}:${identity.site}`;
  const appUrl = identity.appUrl ?? DEFAULT_APP_URL;
  const iat = Math.floor(Date.now() / 1000);
  const exp = iat + TOKEN_LIFETIME_SECONDS;

  const payload = {
    [NAME_IDENTIFIER_CLAIM]: sub,
    app_url: appUrl,
    iat,
    exp,
    aud: config.audience,
    iss: config.issuer,
  };
  const jwt = await signTestJwt(payload, config.secret);

  return new User({
    access_token: jwt,
    id_token: jwt,
    token_type: 'Bearer',
    scope: 'openid profile tlp',
    profile: {
      iss: config.issuer,
      sub,
      aud: config.audience,
      exp,
      iat,
      app_url: appUrl,
    },
    expires_at: exp,
    session_state: null,
  });
};
